/**
 * useAdultMode — single source of truth for "is this account in adult mode?"
 * Returns true when:
 *   - `user.age_band` is 'young_adult' or 'adult', OR
 *   - the saved signup persona in localStorage is 'adult'.
 * Updates reactively when the URL changes (?persona=adult) or localStorage
 * is mutated by the homepage / login fork.
 */
import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

const readPersona = () => {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('persona');
    if (fromUrl === 'adult' || fromUrl === 'teen') return fromUrl;
    return localStorage.getItem('hibff_signup_persona') || '';
  } catch (e) { return ''; }
};

export const useAdultMode = () => {
  const { user } = useAuth();
  const [persona, setPersona] = useState(readPersona);

  useEffect(() => {
    const handler = () => setPersona(readPersona());
    window.addEventListener('storage', handler);
    window.addEventListener('hibff-persona-changed', handler);
    return () => {
      window.removeEventListener('storage', handler);
      window.removeEventListener('hibff-persona-changed', handler);
    };
  }, []);

  const ageBand = (user?.age_band || '').toLowerCase();
  const role = (user?.role || '').toLowerCase();
  // Parents are never in adult mode — they buy family plans for their teens.
  if (role === 'parent') return false;
  // Aug 2026 — a logged-in account's REAL age band always wins. A stale
  // 'adult' persona in localStorage (from browsing the homepage adult fork)
  // was flipping teen accounts into adult mode, mislabelling the sidebar
  // ("Pro Packs") against teen-flavoured pages.
  if (ageBand === 'teen') return false;
  return ageBand === 'young_adult' || ageBand === 'adult' || persona === 'adult';
};

export default useAdultMode;
